// Search analytics for the site-search modal.
//
// Sends events to same-origin Rails endpoints which forward them to Typesense
// /analytics/events using the admin API key. Going through Rails avoids two
// browser-side problems:
//   1. Typesense's /analytics/events endpoint requires a wildcard ("*") key
//      to authorise — we can't safely expose that to the browser.
//   2. Cross-origin sendBeacon to search.hiiker.com would either need a CORS
//      preflight (which sendBeacon does not perform) or a same-origin
//      content-type workaround. Same-origin endpoints sidestep both.
//
// Usage from Search.vue:
//   SearchAnalytics.setQuery(currentQuery);
//   SearchAnalytics.recordClick({ docId, type, position, source });
//   SearchAnalytics.recordBrowse({ lat, lng });   // wildcard+geo searches

const DEVICE_ID_KEY = "hiiker_search_device_id";
const BROWSE_SESSION_KEY_PREFIX = "hiiker_search_browse_fired:";
const MIN_QUERY_LENGTH = 3;
const CLICK_ENDPOINT = "/api/search/click";
const BROWSE_ENDPOINT = "/api/search/browse";

let lastQuery: string = "";

const deviceId = (): string => {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return "anonymous";
  }
};

// sendBeacon-first fire-and-forget. Falls back to keepalive fetch if the
// browser refuses to queue (rare). Never throws, never blocks the caller.
const send = (url: string, body: string): void => {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "text/plain" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // swallow — analytics must never break the calling path
  }
};

export interface SearchClickPayload {
  docId: string;
  type: string;       // "Hike" | "Region" | "Country" | "POI" | "HikeList" | "Recording" | "Location" | "Shortcut"
  position: number;   // 0-based index within the section
  source: string;     // "popular" | "my_hikes" | "recordings" | "pois" | "hikelists" | "locations" | "shortcuts"
}

export interface SearchBrowsePayload {
  lat: number;
  lng: number;
  // Throttle key. Each scope (e.g. "explore_map") fires once per browser
  // session. Multiple scopes don't suppress each other.
  scope?: string;
}

export const SearchAnalytics = {
  setQuery(q: string): void {
    lastQuery = (q || "").trim();
  },

  // Click on a search result. Uses sendBeacon so the request survives the
  // page navigation that happens immediately after the user clicks.
  recordClick(payload: SearchClickPayload): void {
    if (!lastQuery || lastQuery.length < MIN_QUERY_LENGTH) return;

    const body = JSON.stringify({
      q: lastQuery,
      doc_id: payload.docId,
      doc_type: payload.type,
      position: payload.position,
      source: payload.source,
      user_id: deviceId(),
    });

    send(CLICK_ENDPOINT, body);
  },

  // User browsed the map / opened the search modal with a known location
  // but no typed query. Rails reverse-geocodes the lat/lng to a region name
  // and forwards as a synthetic search event so the signal aggregates in
  // popular_queries instead of being lost as another q="*" event.
  //
  // Throttled to once per browser session via sessionStorage — a user's
  // location does not meaningfully change within a session, so additional
  // events would just inflate request volume without adding signal.
  recordBrowse(payload: SearchBrowsePayload): void {
    if (!Number.isFinite(payload.lat) || !Number.isFinite(payload.lng)) return;
    if (payload.lat === 0 && payload.lng === 0) return;

    const scope = payload.scope || "default";
    const sessionKey = `${BROWSE_SESSION_KEY_PREFIX}${scope}`;
    try {
      if (typeof sessionStorage !== "undefined") {
        if (sessionStorage.getItem(sessionKey)) return;
        sessionStorage.setItem(sessionKey, "1");
      }
    } catch {
      // sessionStorage unavailable (private mode, etc) — fall through and fire anyway
    }

    const body = JSON.stringify({
      lat: payload.lat,
      lng: payload.lng,
      user_id: deviceId(),
    });

    send(BROWSE_ENDPOINT, body);
  },
};
